/**
 * Outlook Event Service - Manages calendar events for TeeSync rounds
 *
 * This service calls edge functions to create/update/delete Outlook calendar events.
 * Token management (including refresh) is handled entirely server-side by the edge
 * functions using the shared tokenManager - the frontend never sees access tokens.
 */
import { supabase } from '../lib/supabase';

/**
 * Creates an Outlook calendar event for a round
 * @param {string} roundId - The round ID to create an event for
 * @returns {Promise<Object>} The created event details
 */
export const createOutlookEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    console.log('📅 createOutlookEvent: Invoking edge function with roundId:', roundId, typeof roundId);

    const { data, error } = await supabase.functions.invoke('create-outlook-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    console.log('📅 createOutlookEvent: Response data:', data);
    console.log('📅 createOutlookEvent: Response error:', error);

    if (error) {
      console.error('Error creating Outlook event:', error);
      // Try to extract the actual error message from the response context
      if (error.context) {
        try {
          const errorBody = await error.context.json();
          console.error('📅 Edge function error details:', errorBody);
          throw new Error(errorBody.error || error.message);
        } catch (parseError) {
          // If we can't parse the body, just throw the original error
          console.error('📅 Could not parse error body:', parseError);
        }
      }
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to create Outlook event');
    }

    console.log('✅ Outlook event created:', data.eventId);
    return data;
  } catch (error) {
    console.error('createOutlookEvent error:', error);
    throw error;
  }
};

/**
 * Updates an existing Outlook calendar event for a round
 * @param {string} roundId - The round ID to update the event for
 * @returns {Promise<Object>} The updated event details
 */
export const updateOutlookEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('update-outlook-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Error updating Outlook event:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to update Outlook event');
    }

    console.log('✅ Outlook event updated:', data.eventId);
    return data;
  } catch (error) {
    console.error('updateOutlookEvent error:', error);
    throw error;
  }
};

/**
 * Deletes an Outlook calendar event for a round
 * @param {string} roundId - The round ID to delete the event for
 * @returns {Promise<Object>} Success confirmation
 */
export const deleteOutlookEvent = async (roundId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('delete-outlook-event', {
      body: { roundId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Error deleting Outlook event:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete Outlook event');
    }

    console.log('✅ Outlook event deleted');
    return data;
  } catch (error) {
    console.error('deleteOutlookEvent error:', error);
    throw error;
  }
};

/**
 * Checks if user has Outlook calendar connected and sync enabled
 * @returns {Promise<boolean>} True if user can sync to Outlook
 */
export const canSyncToOutlook = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('📅 canSyncToOutlook: No user found');
      return false;
    }

    const { data: connection, error } = await supabase
      .from('calendar_connections')
      .select('sync_enabled')
      .eq('user_id', user.id)
      .eq('provider', 'outlook')
      .single();

    if (error) {
      console.log('📅 canSyncToOutlook: Error fetching connection:', error.message);
      return false;
    }

    if (!connection) {
      console.log('📅 canSyncToOutlook: No Outlook connection found for user');
      return false;
    }

    console.log('📅 canSyncToOutlook: Connection found, sync_enabled =', connection.sync_enabled);
    return connection.sync_enabled === true;
  } catch (error) {
    console.error('canSyncToOutlook error:', error);
    return false;
  }
};

/**
 * Automatically syncs a round to Outlook calendar if conditions are met
 * This is a convenience function that combines the check and create operations
 * @param {string} roundId - The round ID to sync
 * @returns {Promise<Object|null>} The created event details or null if skipped
 */
export const autoSyncRoundToOutlook = async (roundId) => {
  try {
    console.log('📅 autoSyncRoundToOutlook called for round:', roundId);
    const canSync = await canSyncToOutlook();
    console.log('📅 canSyncToOutlook result:', canSync);

    if (!canSync) {
      console.log('⏭️  Outlook sync skipped: calendar not connected or sync disabled');
      return null;
    }

    console.log('📅 Attempting to create Outlook event for round:', roundId);
    const result = await createOutlookEvent(roundId);
    console.log('📅 createOutlookEvent result:', result);
    return result;
  } catch (error) {
    // Log the error but don't throw - we don't want calendar sync failures to break round acceptance
    console.warn('⚠️  Failed to auto-sync round to Outlook:', error.message);
    console.warn('⚠️  Full error:', error);
    return null;
  }
};
